import { useState } from "react";
import { CheckIcon, ChevronsUpDownIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Chip } from "@/components/badge/chip";
import { cn } from "@/lib/utils";
import type { App } from "@/types/domain";

// Multi-app picker for the RC form (WED-245). Selected apps show as chips in
// the trigger; the list toggles each app (check mark = selected) and stays
// open so several can be picked in one go. One RC is created per app
// server-side, so the picker never implies a single RC spanning apps.
export const RcAppsPicker = ({
  value,
  onChange,
  options,
  disabled,
}: {
  value: string[];
  onChange: (ids: string[]) => void;
  options: App[];
  disabled?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const search = query.trim().toLowerCase();
  const selected = new Set(value);
  const byId = new Map(options.map((a) => [String(a.id), a]));
  const suggestions = options.filter((a) =>
    (a.name ?? "").toLowerCase().includes(search)
  );

  const toggle = (id: string) => {
    onChange(
      selected.has(id) ? value.filter((v) => v !== id) : [...value, id]
    );
  };

  const names = value.map((id) => byId.get(id)?.name ?? id);

  return (
    <div className="space-y-1">
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setQuery("");
        }}
      >
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            role="combobox"
            aria-label="App"
            aria-expanded={open}
            disabled={disabled}
            className="w-full justify-between font-normal"
          >
            <span
              className={cn(
                "truncate",
                !names.length && "text-muted-foreground"
              )}
            >
              {names.length ? names.join(", ") : "Select apps…"}
            </span>
            <ChevronsUpDownIcon className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-(--radix-popover-trigger-width) p-0"
          align="start"
        >
          <Command shouldFilter={false} label="App">
            <CommandInput
              placeholder="Search apps…"
              value={query}
              onValueChange={setQuery}
            />
            <CommandList>
              {suggestions.length === 0 && <CommandEmpty>No apps.</CommandEmpty>}
              <CommandGroup>
                {suggestions.map((a) => {
                  const id = String(a.id);
                  const on = selected.has(id);
                  return (
                    <CommandItem
                      key={id}
                      value={`app ${id}`}
                      data-checked={on}
                      onSelect={() => toggle(id)}
                    >
                      {a.name}
                      <CheckIcon
                        className={cn(
                          "ml-auto h-4 w-4",
                          on ? "opacity-100" : "opacity-0"
                        )}
                      />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {/* Selected apps spelled out (one RC per app after submit). */}
      {names.length > 1 && (
        <div className="flex flex-wrap gap-1" aria-label="Selected apps">
          {value.map((id) => (
            <Chip key={id} tone="neutral">
              {byId.get(id)?.name ?? id}
            </Chip>
          ))}
        </div>
      )}
    </div>
  );
};

export default RcAppsPicker;
